import { create } from 'zustand';
import { supabase } from '@/lib/supabase';

export const notificationCapabilities = {
  messages: ['toast', 'sound', 'desktop'],
  reminders: ['toast', 'sound'],
} as const;
export type NotificationEvent = keyof typeof notificationCapabilities;
export type NotificationChannel = 'toast' | 'sound' | 'desktop';
export type NotificationPreferences = Record<NotificationEvent, Partial<Record<NotificationChannel, boolean>>>;

function ownValue(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const descriptor = Object.getOwnPropertyDescriptor(value, key);
  return descriptor && 'value' in descriptor ? descriptor.value : undefined;
}

export function normalizeNotificationPreferences(value: unknown): NotificationPreferences {
  const result: NotificationPreferences = { messages: {}, reminders: {} };
  for (const event of Object.keys(notificationCapabilities) as NotificationEvent[]) {
    for (const channel of notificationCapabilities[event]) {
      const candidate = ownValue(ownValue(value, event), channel);
      result[event][channel] = typeof candidate === 'boolean' ? candidate : true;
    }
  }
  return result;
}

interface PreferencesState {
  identity: string | null;
  preferences: NotificationPreferences;
  status: 'idle' | 'loading' | 'ready' | 'error';
  saving: boolean;
  load: (identity: string) => Promise<void>;
  save: (identity: string, value: NotificationPreferences) => Promise<void>;
  clear: () => void;
}
let epoch = 0;
let revision = 0;
let loading: Promise<void> | null = null;
const presented = new Set<string>();

export const useNotificationPreferencesStore = create<PreferencesState>((set, get) => ({
  identity: null, preferences: normalizeNotificationPreferences(null), status: 'idle', saving: false,
  clear: () => {
    epoch++;
    revision++;
    loading = null;
    presented.clear();
    set({ identity: null, preferences: normalizeNotificationPreferences(null), status: 'idle', saving: false });
  },
  load: (identity) => {
    if (get().identity === identity && get().status === 'ready') return Promise.resolve();
    if (get().identity === identity && loading) return loading;
    get().clear();
    const requestEpoch = epoch;
    set({ identity, status: 'loading' });
    const promise = (async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        if (requestEpoch !== epoch) return;
        if (error || data.user?.id !== identity) throw new Error('Não foi possível carregar suas preferências. Tente novamente.');
        set({ preferences: normalizeNotificationPreferences(data.user.user_metadata?.notification_preferences), status: 'ready' });
      } catch {
        if (requestEpoch === epoch) set({ status: 'error' });
      } finally {
        if (requestEpoch === epoch) loading = null;
      }
    })();
    loading = promise;
    return promise;
  },
  save: async (identity, value) => {
    if (get().identity !== identity || get().status !== 'ready' || get().saving) throw new Error('Recarregue suas preferências antes de salvar.');
    const requestEpoch = epoch;
    const requestRevision = ++revision;
    const preferences = normalizeNotificationPreferences(value);
    set({ saving: true });
    try {
      // Auth API merges this metadata key; no role, tenant or arbitrary user ID is writable here.
      const { data, error } = await supabase.auth.updateUser({ data: { notification_preferences: preferences } });
      if (requestEpoch !== epoch || requestRevision !== revision) throw new Error('A sessão mudou.');
      if (error || data.user?.id !== identity) throw new Error('Preferências não confirmadas. Tente novamente.');
      const persisted = ownValue(data.user.user_metadata, 'notification_preferences');
      const confirmed = normalizeNotificationPreferences(persisted);
      const complete = (Object.keys(notificationCapabilities) as NotificationEvent[]).every(event =>
        notificationCapabilities[event].every(channel => typeof ownValue(ownValue(persisted, event), channel) === 'boolean'));
      if (!complete || JSON.stringify(confirmed) !== JSON.stringify(preferences)) throw new Error('Preferências não confirmadas. Recarregue e tente novamente.');
      set({ preferences: confirmed });
    } finally {
      if (requestEpoch === epoch && requestRevision === revision) set({ saving: false });
    }
  },
}));

export function notificationAllowed(identity: string | null | undefined, event: NotificationEvent, channel: NotificationChannel): boolean {
  const state = useNotificationPreferencesStore.getState();
  // Do not present alerts under another identity or before preferences are resolved.
  return !!identity && state.identity === identity && state.status === 'ready' && state.preferences[event]?.[channel] === true;
}

export function claimNotification(identity: string, event: NotificationEvent, id: string): boolean {
  if (!id || useNotificationPreferencesStore.getState().identity !== identity) return false;
  const key = `${identity}:${event}:${id}`;
  if (presented.has(key)) return false;
  presented.add(key);
  // Session-only bounded dedup; notification content is never persisted in browser storage.
  if (presented.size > 2000) presented.delete(presented.values().next().value!);
  return true;
}
